import React from 'react';
import { 
  DollarSign, Building2, Code, 
  Shield, Check, ArrowRight,
  BarChart2, Users, Zap,
  FileText, Globe, Lock, Cpu
} from 'lucide-react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEO/SEOHead';

const ProductsPage = () => {
  const products = [
    {
      id: 'patent-valuation',
      title: 'Patent Valuation',
      description: 'Get accurate, AI-powered valuations for your patents with comprehensive analysis and market insights.',
      icon: DollarSign,
      features: [
        'Instant patent valuation',
        'Market size analysis',
        'Technology significance assessment',
        'Competitive landscape analysis',
        'Licensing potential evaluation'
      ],
      cta: 'Try Patent Valuation',
      href: '/patent-valuation'
    },
    {
      id: 'company-valuation',
      title: 'Company Valuation',
      description: 'Comprehensive company valuation based on patent portfolio, market position, and financial metrics.',
      icon: Building2,
      features: [
        'Patent portfolio analysis',
        'Market position assessment',
        'Competitor benchmarking',
        'Revenue projections',
        'Investment potential analysis'
      ],
      cta: 'Try Company Valuation',
      href: '/company-valuation'
    },
    {
      id: 'api-access',
      title: 'API Access',
      description: 'Integrate our valuation services directly into your applications with our powerful API.',
      icon: Cpu,
      features: [
        'RESTful API endpoints',
        'Real-time valuations',
        'Bulk processing capabilities',
        'Comprehensive documentation',
        'Developer support'
      ],
      cta: 'Contact Sales',
      href: '/contact'
    },
    {
      id: 'enterprise',
      title: 'Enterprise Solutions',
      description: 'Custom solutions for large organizations with advanced features and dedicated support.',
      icon: Shield,
      features: [
        'Custom integration',
        'Dedicated support team',
        'Advanced analytics',
        'Bulk processing',
        'Custom reporting'
      ],
      cta: 'Contact Sales',
      href: '/contact'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <SEOHead 
        title="Patent Valuation Products & Services | IP Analytics Solutions"
        description="Comprehensive patent valuation products: AI-powered patent assessment, company valuation, API access, and enterprise solutions. Professional IP analytics for investors and IP professionals."
        keywords="patent valuation products, IP analytics services, patent assessment solutions, company valuation services, patent API, enterprise IP solutions, intellectual property products"
        ogTitle="Patent Valuation Products & IP Analytics Services"
        ogDescription="Professional patent valuation products and IP analytics services. From individual patent assessments to enterprise solutions."
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Patent Valuation Products",
          "description": "Comprehensive suite of patent valuation and IP analytics products for professionals and organizations",
          "provider": {
            "@type": "Organization",
            "name": "PricePatent"
          },
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Patent Valuation Services",
            "itemListElement": products.map(product => ({
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": product.title,
                "description": product.description
              }
            }))
          }
        }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center px-4 py-2 bg-blue-50 rounded-full text-blue-600 text-sm font-medium mb-4">
            <Cpu className="h-4 w-4 mr-2" />
            Our Products
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            <span className="block">Comprehensive IP Valuation</span>
            <span className="block bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">Solutions</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Choose from our range of powerful tools and services designed to help you understand and maximize the value of your intellectual property.
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {products.map((product) => {
            const Icon = product.icon;
            return (
              <div key={product.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow duration-300">
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mr-4">
                      <Icon className="h-6 w-6 text-blue-600" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900">{product.title}</h2>
                  </div>
                  
                  <p className="text-gray-600 mb-6">{product.description}</p>
                  
                  <div className="space-y-3 mb-6">
                    {product.features.map((feature, index) => (
                      <div key={index} className="flex items-center">
                        <div className="w-5 h-5 bg-green-50 rounded-full flex items-center justify-center mr-3">
                          <Check className="h-3 w-3 text-green-600" />
                        </div>
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {product.id === 'patent-valuation' && (
                    <Link
                      to="/demo?type=patent"
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 transition-all duration-300 shadow-sm hover:shadow-md"
                    >
                      Try Patent Valuation
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  )}

                  {product.id === 'company-valuation' && (
                    <Link
                      to="/demo?type=company"
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 transition-all duration-300 shadow-sm hover:shadow-md"
                    >
                      Try Company Valuation
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  )}

                  {product.id === 'api-access' && (
                    <Link
                      to="/contact"
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 transition-all duration-300 shadow-sm hover:shadow-md"
                    >
                      Contact Sales
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  )}

                  {product.id === 'enterprise' && (
                    <Link
                      to="/contact"
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 transition-all duration-300 shadow-sm hover:shadow-md"
                    >
                      Contact Sales
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Why Choose Us Section */}
        <div className="mt-16 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl shadow-sm overflow-hidden border border-blue-100">
          <div className="p-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Why Choose PricePatent</h2>
              <p className="text-gray-600">Experience the difference with our cutting-edge IP valuation platform</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-3">
                    <Zap className="h-5 w-5 text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Lightning Fast</h3>
                </div>
                <p className="text-gray-600">Get comprehensive valuations in seconds, not days. Our AI-powered platform delivers instant results without compromising accuracy.</p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-3">
                    <BarChart2 className="h-5 w-5 text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Data-Driven Insights</h3>
                </div>
                <p className="text-gray-600">Access detailed market analysis, comparable assets, and growth projections backed by comprehensive data and AI analysis.</p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-3">
                    <Shield className="h-5 w-5 text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Trusted by Experts</h3>
                </div>
                <p className="text-gray-600">Join leading IP professionals, investors, and corporations who rely on our platform for accurate and reliable patent valuations.</p>
              </div>
            </div>

            <div className="mt-8 text-center">
              <Link
                to="/demo"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 transition-all duration-300 shadow-sm hover:shadow-md"
              >
                Try It Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductsPage; 